import { defineStore } from 'pinia'
import { reactive } from 'vue'
import { COMMENT } from '@/_core/endpoint'
import apiRequestManager from '@/_core/apiRequestManager'
import { useToastStore } from '@/stores/toast'
import { useBookmarkStore } from '@/stores/bookmark'
import { useTimelineStore } from '@/stores/timeline'

export const useCommentStore = defineStore('comment', () => {
  const toastStore = useToastStore()
  const bookmarkStore = useBookmarkStore()
  const timelineStore = useTimelineStore()

  const context: CommentStoreContext = reactive({
    local: {},
  })

  const commentMap = new Map<string, CommentHistoryItem>()

  /**
   * コメントの検索を高速化するため、ページ内の全コメントオブジェクトを Map 化して確保しておく
   */
  const initComment = () => {
    // 投稿・編集・削除のたびに再マッピングされるため、前回の（削除済みを含む）エントリが残らないようクリアする
    commentMap.clear()

    // 初期化時にマッピング
    if (context.local.myComment) {
      commentMap.set(context.local.myComment.id, context.local.myComment)
      context.local.myComment.replies?.forEach(rep => commentMap.set(rep.id, rep))
    }

    context.local.logs?.forEach(log => {
      commentMap.set(log.id, log)
      log.replies?.forEach(rep => commentMap.set(rep.id, rep))
    })
  }

  const getComment = (commentId: string) => {
    return commentMap.get(commentId)
  }

  const fetchComments = async (contentType: CommentContentType, contentId: number) => {
    const endpoint = createFetchEndpoint(contentType, contentId)

    try {
      await apiRequestManager.handleRequest({
        endpoint,
        callback: (response: Response<CommentLogResponse>, isSuccess: boolean) => {
          if (isSuccess) {

            const logs: CommentHistoryItem[] = []
            const myComment: CommentHistoryItem = { id: '', body: '' }

            if (response.data.my_comment && response.data.my_comment.id) {
              Object.assign(myComment, {
                id: response.data.my_comment.id,
                body: response.data.my_comment.comment_body,
                date: response.data.my_comment.comment_created_at,
                user: {
                  id: response.data.my_comment.contributor.user_id,
                  displayName: response.data.my_comment.contributor.name,
                  avatarUrl: response.data.my_comment.contributor.image_url,
                  message: response.data.my_comment.contributor.comment,
                  introduction: response.data.my_comment.contributor.introduction,
                  companyName: response.data.my_comment.contributor.company_name,
                  departmentAndPosition: response.data.my_comment.contributor.position_name,
                },
                liked: {
                  total: response.data.my_comment.liked_count || 0,
                  byMe: response.data.my_comment.liked_by_me || false,
                },
                replies: response.data.my_comment.replies.map(rep => {
                  return {
                    id: rep.id,
                    body: rep.comment_body,
                    date: rep.comment_created_at,
                    user: {
                      id: rep.contributor.user_id,
                      displayName: rep.contributor.name,
                      avatarUrl: rep.contributor.image_url,
                      message: rep.contributor.comment,
                      introduction: rep.contributor.introduction,
                      companyName: rep.contributor.company_name,
                      departmentAndPosition: rep.contributor.position_name,
                    },
                    liked: {
                      total: rep.liked_count || 0,
                      byMe: rep.liked_by_me || false,
                    },
                  }
                }),
              })
            }

            response.data.comments.forEach((v) => {
              logs.push({
                id: v.id,
                body: v.comment_body,
                date: v.comment_created_at,
                user: {
                  id: v.contributor.user_id,
                  displayName: v.contributor.name,
                  avatarUrl: v.contributor.image_url,
                  message: v.contributor.comment,
                  introduction: v.contributor.introduction,
                  companyName: v.contributor.company_name,
                  departmentAndPosition: v.contributor.position_name,
                },
                liked: {
                  total: v.liked_count || 0,
                  byMe: v.liked_by_me || false,
                },
                replies: v.replies.map(rep => {
                  return {
                    id: rep.id,
                    body: rep.comment_body,
                    date: rep.comment_created_at,
                    byMe: rep.contributor.me,
                    user: {
                      id: rep.contributor.user_id,
                      displayName: rep.contributor.name,
                      avatarUrl: rep.contributor.image_url,
                      message: rep.contributor.comment,
                      introduction: rep.contributor.introduction,
                      companyName: rep.contributor.company_name,
                      departmentAndPosition: rep.contributor.position_name,
                    },
                    liked: {
                      total: rep.liked_count || 0,
                      byMe: rep.liked_by_me || false,
                    },
                  }
                }),
              })
            })

            const total = (myComment.id ? 1 + (myComment.replies?.length ?? 0) : 0) +
              logs.reduce((sum, log) => sum + 1 + (log.replies?.length ?? 0), 0)

            context.local = {
              myComment,
              logs,
              total,
            }

            bookmarkStore.setBookmark({
              isActive: response.data.article.liked_by_me,
              total: response.data.article.liked_count,
            })

            initComment()
          }
        }
      })
    } catch (error) {
      console.error(error)
    }

  }

  /**
   * コメントの投稿
   * @param contentType 記事種別
   * @param contentId 記事 ID
   * @param body コメント本文
   * @param parentId 返信の場合のリプライ元コメントの ID
   * @param twitterShareUrl シェア用 Twitter(X) URL
   * @param facebookShareUrl シェア用 Facebook URL
   * @returns 投稿に成功したかどうか
   */
  const postComment = async (contentType: CommentContentType, contentId: number, body: string, parentId?: string, twitterShareUrl?: string, facebookShareUrl?: string) => {
    if (!contentId || !body) return

    const endpoint = createPostEndpoint(contentType, contentId)

    const params = {
      body,
      tw_share_url: twitterShareUrl ?? '',
      fb_share_url: facebookShareUrl ?? '',
    }

    if (parentId) Object.assign(params, { id: parentId })

    const errorToast: ToastItem = {
      id: `toast-${new Date().getTime()}`,
      messageType: 'alert',
      message: '投稿に失敗しました',
    }

    let _isSuccess = false
    try {
      await apiRequestManager.handleRequest({
        endpoint,
        params,
        callback: async (response: Response<any>, isSuccess: boolean) => {
          _isSuccess = isSuccess

          if (isSuccess) {
            await fetchComments(contentType, contentId)
            await timelineStore.fetchTimeline()
          } else {
            toastStore.setToastItem(errorToast)
          }
        }
      })
    } catch (error) {
      toastStore.setToastItem(errorToast)
      _isSuccess = false
    }

    return _isSuccess
  }

  /**
   * コメントの編集
   * @param contentType 記事種別
   * @param contentId 記事 ID
   * @param commentId コメント ID
   * @param body コメント本文
   * @returns 編集に成功したかどうか
   */
  const editComment = async (contentType: CommentContentType, contentId: number, commentId: string, body: string) => {
    if (!contentId || !commentId || !body) return

    const path = COMMENT.EDIT.path({
      id: commentId,
    })

    const endpoint = { ...COMMENT.EDIT, path }

    const params = {
      body,
    }

    const errorToast: ToastItem = {
      id: `toast-${new Date().getTime()}`,
      messageType: 'alert',
      message: '編集に失敗しました',
    }

    let _isSuccess = false
    try {
      await apiRequestManager.handleRequest({
        endpoint,
        params,
        callback: async (response: Response<any>, isSuccess: boolean) => {
          _isSuccess = isSuccess

          if (isSuccess) {
            await fetchComments(contentType, contentId)
            await timelineStore.fetchTimeline()
          } else {
            toastStore.setToastItem(errorToast)
          }
        }
      })
    } catch (error) {
      toastStore.setToastItem(errorToast)
      _isSuccess = false
    }

    return _isSuccess
  }

  /**
   * コメントの削除
   * @param contentType 記事種別
   * @param contentId 記事 ID
   * @param commentId コメント ID
   * @returns 削除に成功したかどうか
   */
  const deleteComment = async (contentType: CommentContentType, contentId: number, commentId: string) => {
    if (!contentId || !commentId) return

    const path = COMMENT.DELETE.path({
      id: commentId,
    })

    const endpoint = { ...COMMENT.DELETE, path }

    const errorToast: ToastItem = {
      id: `toast-${new Date().getTime()}`,
      messageType: 'alert',
      message: '削除に失敗しました',
    }

    let _isSuccess = false
    try {
      await apiRequestManager.handleRequest({
        endpoint,
        callback: async (response: Response<any>, isSuccess: boolean) => {
          _isSuccess = isSuccess

          if (isSuccess) {
            await fetchComments(contentType, contentId)
            await timelineStore.fetchTimeline()
          } else {
            toastStore.setToastItem(errorToast)
          }
        }
      })
    } catch (error) {
      toastStore.setToastItem(errorToast)
      _isSuccess = false
    }

    return _isSuccess
  }

  /**
   * コメントの「いいね」機能
   * @param id 対象のコメント id
   */
  const likeComment = async (commentId: string) => {
    const path = COMMENT.LIKE.path({
      commentId: commentId
    })
    const endpoint = { ...COMMENT.LIKE, path: path }

    let _isSuccess

    try {
      await apiRequestManager.handleRequest({
        endpoint,
        callback: (_: any, isSuccess: boolean) => {
          _isSuccess = isSuccess
        }
      })
    } catch (error) {
      console.error(error)
      _isSuccess = undefined
    }

    return _isSuccess
  }

  function createFetchEndpoint (contentType: CommentContentType, contentId: number) {
    return contentType === 'article'
      ? { ...COMMENT.ARTICLE.FETCH, path: COMMENT.ARTICLE.FETCH.path({ articleId: contentId }) }
      : { ...COMMENT.REPORT.FETCH, path: COMMENT.REPORT.FETCH.path({ reportArticleId: contentId }) }
  }

  function createPostEndpoint (contentType: CommentContentType, contentId: number) {
    return contentType === 'article'
      ? { ...COMMENT.ARTICLE.POST, path: COMMENT.ARTICLE.POST.path({ articleId: contentId }) }
      : { ...COMMENT.REPORT.POST, path: COMMENT.REPORT.POST.path({ reportArticleId: contentId }) }
  }

  return {
    // data
    context,

    // methods
    initComment,
    getComment,
    fetchComments,
    postComment,
    editComment,
    deleteComment,
    likeComment,
  }
})
